import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import { dashboardAPI } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ArrowLeft, Search, Filter, ClipboardList, X, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import logger from '@/lib/logger';

const STATUS_LABELS = {
  not_started: 'Not Started',
  documents_pending: 'Docs Pending',
  documents_received: 'Docs Received',
  further_docs_required: 'Further Docs Required',
  ready_for_review: 'Ready for Review',
  tax_payable: 'Tax Payable',
  ready_to_file: 'Ready to File',
  filed: 'Filed',
};

const STATUS_COLORS = {
  not_started: 'bg-gray-100 text-gray-600',
  documents_pending: 'bg-amber-100 text-amber-700',
  documents_received: 'bg-blue-100 text-blue-700',
  further_docs_required: 'bg-orange-100 text-orange-700',
  ready_for_review: 'bg-purple-100 text-purple-700',
  tax_payable: 'bg-rose-100 text-rose-700',
  ready_to_file: 'bg-indigo-100 text-indigo-700',
  filed: 'bg-green-100 text-green-700',
};

const MyTasksPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [boardFilter, setBoardFilter] = useState('all');
  const [sort, setSort] = useState({ col: 'client_name', dir: 'asc' });

  const toggleSort = (col) => {
    setSort(prev => prev.col === col ? { col, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'asc' });
  };

  useEffect(() => {
    dashboardAPI.myTasks()
      .then(res => setItems(res.data || []))
      .catch(err => logger.error('Failed to load my tasks', err))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Unique boards for filter
  const boards = useMemo(() => {
    const map = {};
    items.forEach(i => { if (i.board_id && i.board_name) map[i.board_id] = i.board_name; });
    return Object.entries(map).map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [items]);

  // Status counts
  const statusCounts = useMemo(() => {
    const counts = {};
    items.forEach(i => { counts[i.status] = (counts[i.status] || 0) + 1; });
    return counts;
  }, [items]);

  // Filtered + sorted items
  const filtered = useMemo(() => {
    const matched = items.filter(i => {
      if (search) {
        const q = search.toLowerCase();
        if (![i.client_name, i.subtask_title, i.task_title, i.board_name].some(v => v?.toLowerCase().includes(q))) return false;
      }
      if (statusFilter !== 'all' && i.status !== statusFilter) return false;
      if (boardFilter !== 'all' && i.board_id !== boardFilter) return false;
      return true;
    });
    const { col, dir } = sort;
    if (!col) return matched;
    const sign = dir === 'asc' ? 1 : -1;
    return [...matched].sort((a, b) => {
      const va = (a[col] || '').toString().toLowerCase();
      const vb = (b[col] || '').toString().toLowerCase();
      return va.localeCompare(vb, undefined, { numeric: true }) * sign;
    });
  }, [items, search, statusFilter, boardFilter, sort]);

  if (loading) {
    return <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="h-16 bg-muted rounded-xl animate-pulse" />)}</div>;
  }

  return (
    <div className="space-y-6" data-testid="my-tasks-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <ClipboardList className="h-5 w-5 text-primary" />
            <h1 className="text-xl font-semibold">My Tasks</h1>
          </div>
          <p className="text-sm text-muted-foreground mt-1">{items.length} assignments across {boards.length} boards</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {Object.entries(statusCounts).map(([status, count]) => (
            <Badge
              key={status}
              variant="secondary"
              className={`text-xs cursor-pointer ${statusFilter === status ? 'ring-2 ring-primary' : ''} ${STATUS_COLORS[status] || 'bg-gray-100'}`}
              onClick={() => setStatusFilter(statusFilter === status ? 'all' : status)}
              data-testid={`status-badge-${status}`}
            >
              {STATUS_LABELS[status] || status}: {count}
            </Badge>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search client, subtask, task, board..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9"
            data-testid="my-tasks-search"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px] h-9 text-sm" data-testid="my-tasks-status-filter">
            <Filter className="h-3.5 w-3.5 mr-1.5" />
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses ({items.length})</SelectItem>
            {Object.entries(STATUS_LABELS).map(([key, label]) => {
              const count = statusCounts[key] || 0;
              if (count === 0) return null;
              return <SelectItem key={key} value={key}>{label} ({count})</SelectItem>;
            })}
          </SelectContent>
        </Select>
        <Select value={boardFilter} onValueChange={setBoardFilter}>
          <SelectTrigger className="w-[180px] h-9 text-sm" data-testid="my-tasks-board-filter">
            <SelectValue placeholder="All Boards" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Boards</SelectItem>
            {boards.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
          </SelectContent>
        </Select>
        {(search || statusFilter !== 'all' || boardFilter !== 'all') && (
          <Button variant="ghost" size="sm" className="h-9 text-xs gap-1" onClick={() => { setSearch(''); setStatusFilter('all'); setBoardFilter('all'); }}>
            <X className="h-3.5 w-3.5" /> Clear
          </Button>
        )}
        <span className="text-sm text-muted-foreground ml-auto">{filtered.length} result(s)</span>
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <ClipboardList className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
            <p className="text-muted-foreground">{items.length === 0 ? 'No tasks assigned to you yet.' : 'No tasks match your filters.'}</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                {[
                  { key: 'board_name', label: 'Board' },
                  { key: 'task_title', label: 'Task' },
                  { key: 'subtask_title', label: 'Subtask' },
                  { key: 'client_name', label: 'Client' },
                  { key: 'status', label: 'Status' },
                ].map(col => (
                  <TableHead key={col.key} className="cursor-pointer select-none hover:bg-muted/50" onClick={() => toggleSort(col.key)} data-testid={`my-tasks-sort-${col.key}`}>
                    <span className={`flex items-center gap-1 ${sort.col === col.key ? 'text-foreground' : 'text-muted-foreground'}`}>
                      {col.label}
                      {sort.col === col.key
                        ? (sort.dir === 'asc' ? <ArrowUp className="h-3.5 w-3.5 text-primary" /> : <ArrowDown className="h-3.5 w-3.5 text-primary" />)
                        : <ArrowUpDown className="h-3.5 w-3.5 opacity-70" />}
                    </span>
                  </TableHead>
                ))}
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((item, idx) => (
                <TableRow key={`${item.subtask_id}-${item.client_id}-${idx}`} data-testid="my-task-row">
                  <TableCell>
                    <Badge variant="outline" className="text-xs">{item.board_name}</Badge>
                  </TableCell>
                  <TableCell className="text-sm">{item.task_title}</TableCell>
                  <TableCell className="text-sm font-medium">{item.subtask_title}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center text-[10px] font-medium text-primary flex-shrink-0">
                        {item.client_name?.[0]?.toUpperCase()}
                      </div>
                      <span className="text-sm">{item.client_name}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={`text-xs ${STATUS_COLORS[item.status] || 'bg-gray-100'}`}>
                      {STATUS_LABELS[item.status] || item.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs"
                      onClick={() => navigate(`/dashboard/boards/${item.board_id}/task/${item.task_id}/subtask/${item.subtask_id}`)}
                      data-testid={`view-task-${item.subtask_id}`}
                    >
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
};

export default MyTasksPage;
